import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { apiService } from "@/services/api";
import { toast } from "sonner";
import { Shield, QrCode, Key, Copy, Check, AlertCircle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface TwoFactorAuthProps {
  userId: string;
}

export function TwoFactorAuth({ userId }: TwoFactorAuthProps) {
  const { user } = useAuth();
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [setupData, setSetupData] = useState<{
    secret: string;
    otp_auth_url: string;
    qr_code: string;
    recovery_codes: string[];
  } | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState<string[]>([]);

  useEffect(() => {
    checkStatus();
  }, [userId]);

  const checkStatus = async () => {
    try {
      setIsCheckingStatus(true);
      const response = await apiService.getTwoFactorStatus(userId);
      if (response.type === 0) {
        setIsEnabled(response.data.enabled || response.data.is_2fa_enabled);
      }
    } catch (error) {
      console.error("Error checking 2FA status:", error);
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleEnable = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.enableTwoFactor(userId);
      if (response.type === 0) {
        setSetupData(response.data);
        setShowSetupDialog(true);
        toast.success("2FA setup initiated. Please scan the QR code.");
      } else {
        toast.error(response.message || "Failed to enable 2FA");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to enable 2FA");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiService.verifyTwoFactor(userId, verificationCode);
      if (response.type === 0) {
        setIsEnabled(true);
        setShowSetupDialog(false);
        setVerificationCode("");
        setShowRecoveryCodes(true);
        toast.success("Two-factor authentication enabled successfully!");
      } else {
        toast.error(response.message || "Invalid verification code");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to verify code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!confirm("Are you sure you want to disable two-factor authentication? This will make your account less secure.")) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiService.disableTwoFactor(userId);
      if (response.type === 0) {
        setIsEnabled(false);
        setSetupData(null);
        toast.success("Two-factor authentication disabled");
      } else {
        toast.error(response.message || "Failed to disable 2FA");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to disable 2FA");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string, code?: string) => {
    navigator.clipboard.writeText(text);
    if (code) {
      setCopiedCodes([...copiedCodes, code]);
      setTimeout(() => {
        setCopiedCodes(copiedCodes.filter(c => c !== code));
      }, 2000);
    } else {
      toast.success("Copied to clipboard");
    }
  };

  if (isCheckingStatus) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-blue-600" />
              <div>
                <CardTitle>Two-Factor Authentication</CardTitle>
                <CardDescription>
                  Add an extra layer of security to your account
                </CardDescription>
              </div>
            </div>
            <Switch checked={isEnabled} onCheckedChange={isEnabled ? handleDisable : handleEnable} disabled={isLoading} />
          </div>
        </CardHeader>
        <CardContent>
          {isEnabled ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                <Check className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">2FA is enabled</p>
                  <p className="text-xs text-green-700 mt-1">
                    Your account is protected with two-factor authentication. You'll need to enter a code from your authenticator app when logging in.
                  </p>
                </div>
              </div>
              <Button
                variant="destructive"
                onClick={handleDisable}
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? "Disabling..." : "Disable Two-Factor Authentication"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-900">2FA is disabled</p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Enable two-factor authentication to add an extra layer of security to your account.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleEnable}
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? "Setting up..." : "Enable Two-Factor Authentication"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app, then enter the verification code.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* QR Code */}
            {setupData?.qr_code && (
              <div className="flex flex-col items-center space-y-4">
                <div className="p-4 bg-white rounded-lg border-2 border-gray-200">
                  <img
                    src={setupData.qr_code}
                    alt="QR Code"
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-xs text-gray-600 text-center">
                  Scan this QR code with an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator
                </p>
              </div>
            )}

            {/* Manual Entry */}
            {setupData?.secret && (
              <div className="space-y-2">
                <Label>Or enter this code manually:</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={setupData.secret}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(setupData.secret)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <Separator />

            {/* Verification Code Input */}
            <div className="space-y-2">
              <Label htmlFor="verification-code">Enter verification code</Label>
              <Input
                id="verification-code"
                type="text"
                placeholder="000000"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                className="text-center text-2xl font-mono tracking-widest"
              />
              <p className="text-xs text-gray-500">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSetupDialog(false);
                  setVerificationCode("");
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleVerify}
                disabled={isLoading || verificationCode.length !== 6}
                className="flex-1"
              >
                {isLoading ? "Verifying..." : "Verify & Enable"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recovery Codes Dialog */}
      <Dialog open={showRecoveryCodes} onOpenChange={setShowRecoveryCodes}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save Your Recovery Codes</DialogTitle>
            <DialogDescription>
              These codes can be used to access your account if you lose access to your authenticator app. Save them in a safe place.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {setupData?.recovery_codes && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 rounded-lg border">
                  {setupData.recovery_codes.map((code, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-white rounded border"
                    >
                      <code className="text-sm font-mono">{code}</code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(code, code)}
                        className="h-6 w-6 p-0"
                      >
                        {copiedCodes.includes(code) ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(setupData.recovery_codes.join("\n"))}
                  className="w-full"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy All Codes
                </Button>
              </div>
            )}
            <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
              <p className="text-xs text-yellow-800">
                <strong>Important:</strong> These codes are only shown once. Make sure to save them securely. You won't be able to see them again.
              </p>
            </div>
            <Button
              onClick={() => {
                setShowRecoveryCodes(false);
                setSetupData(null);
              }}
              className="w-full"
            >
              I've Saved My Codes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

